import React, { useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileText,
  Printer,
  Download,
  BadgeCheck,
  MapPin,
  Phone,
  Calendar,
  Package,
  Receipt,
} from "lucide-react";
import type { TOrderWithRelation } from "@/types/order-type";

// ---------------- helpers ----------------
const fmtMoney = (v: unknown, currency = "USD") => {
  const n = typeof v === "string" ? Number(v) : (v as number | undefined);
  if (n == null || Number.isNaN(n)) return "-";
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    n
  );
};

const fmtDateTime = (iso?: string | Date | null) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const openPrintWindow = (html: string, title = "Print") => {
  // Build a full HTML document string
  const docHtml = `<!DOCTYPE html><html><head><title>${title}</title>
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <style>
      * { box-sizing: border-box; }
      body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"; margin: 0; padding: 16px; }
      .sr-only{ position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0; }
      @media print { .no-print { display: none !important; } }
    </style>
  </head><body>${html}</body></html>`;

  // Strategy A: popup window + onload (best user experience when not blocked)
  let popup: Window | null = null;
  try {
    popup = window.open("", "_blank", "width=800,height=900,noopener");
  } catch { }

  if (popup) {
    try {
      popup.document.open();
      popup.document.write(docHtml);
      popup.document.close();
      // Wait for the new document to finish layout before printing
      const doPrint = () => {
        try {
          popup!.focus();
          popup!.print();
        } catch { }
        // Some browsers ignore auto-close; do not force-close if user cancels
      };
      // Use DOMContentLoaded + a fallback timeout in case it doesn't fire
      popup.addEventListener("load", doPrint);
      setTimeout(doPrint, 500);
      return;
    } catch {
      // fall through to iframe approach
    }
  }

  // Strategy B: hidden iframe (works even if popups are blocked)
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) return;

  doc.open();
  doc.write(docHtml);
  doc.close();

  const cleanUp = () => {
    setTimeout(() => {
      try {
        document.body.removeChild(iframe);
      } catch { }
    }, 1000);
  };

  const printFromIframe = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      cleanUp();
    }
  };

  // Ensure layout has happened before printing
  iframe.onload = printFromIframe;
  // Fallback in case onload doesn't fire
  setTimeout(printFromIframe, 500);
};

// ---------------- InvoiceDialog (view + print) ----------------
export function InvoiceDialog({
  open,
  onOpenChange,
  data,
}: {
  open: boolean;
  onOpenChange?: (o: boolean) => void;
  data?: TOrderWithRelation | null;
}) {
  const order = data?.order;
  const customer = data?.customer;
  const items = data?.orderItems ?? [];

  const created = order?.createdAt;

  const computedSubtotal = items.reduce((acc, it) => {
    const q = Number(it.quantity ?? 0);
    const p = Number(it.price ?? 0);
    const line = it.amount != null ? Number(it.amount) : q * p;
    return acc + (Number.isFinite(line) ? line : 0);
  }, 0);

  const subtotal = order?.subTotal ?? computedSubtotal;
  const deliveryFee = Number(order?.deliveryFee ?? 0);
  const discountValue = order?.discountPercentage
    ? (Number(subtotal) * Number(order.discountPercentage)) / 100
    : Number(order?.discountPercentage ?? 0); //add discount amount
  const total =
    order?.totalAmount ?? Number(subtotal) + deliveryFee - discountValue;

  const printableInvoiceHtml = useMemo(() => {
    return `
      <div style="max-width:920px;margin:0 auto">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
          <div style="font-size:20px;font-weight:700;display:flex;align-items:center;gap:8px">
            <span>INVOICE</span>
          </div>
          <div style="text-align:right;font-size:12px;color:#64748b">
            <div>Invoice #: ${order?.id ?? "-"}</div>
            <div>Date: ${fmtDateTime(created ?? new Date())}</div>
          </div>
        </div>

        <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:16px">
          <div style="flex:1;min-width:260px;border:1px solid #e5e7eb;border-radius:8px;padding:12px">
            <div style="font-weight:600;margin-bottom:4px">Bill To</div>
            <div>${customer?.name ?? "-"}</div>
            <div style="color:#64748b">${customer?.phone ?? ""}</div>
            <div style="white-space:pre-line;color:#64748b">${customer?.address ?? ""
      }</div>
          </div>
          <div style="flex:1;min-width:260px;border:1px solid #e5e7eb;border-radius:8px;padding:12px">
            <div style="font-weight:600;margin-bottom:4px">Meta</div>
            <div>Status: ${order?.status ?? "-"}</div>
            <div>Payment: ${order?.paymentStatus ?? "-"}</div>
            <div>Shipping:  ${order?.trackingNumber ? `(#${order.trackingNumber})` : ""
      }</div>
          </div>
        </div>

        <table style="width:100%;border-collapse:collapse;font-size:12px">
          <thead>
            <tr>
              <th style="text-align:left;padding:8px;border-bottom:1px solid #e5e7eb">Product</th>
              <th style="text-align:right;padding:8px;border-bottom:1px solid #e5e7eb">Qty</th>
              <th style="text-align:right;padding:8px;border-bottom:1px solid #e5e7eb">Unit</th>
              <th style="text-align:right;padding:8px;border-bottom:1px solid #e5e7eb">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${items
        .map((it) => {
          const qty = Number(it.quantity ?? 0);
          const price = Number(it.price ?? 0);
          const line =
            it.amount != null ? Number(it.amount) : qty * price;
          const name = (it as any).productName ?? it.productId ?? "-";
          return `
                <tr>
                  <td style="padding:8px;border-bottom:1px solid #f1f5f9">${name}</td>
                  <td style="padding:8px;text-align:right;border-bottom:1px solid #f1f5f9">${qty}</td>
                  <td style="padding:8px;text-align:right;border-bottom:1px solid #f1f5f9">${fmtMoney(
            price
          )}</td>
                  <td style="padding:8px;text-align:right;border-bottom:1px solid #f1f5f9">${fmtMoney(
            line
          )}</td>
                </tr>`;
        })
        .join("")}
          </tbody>
        </table>

        <div style="display:flex;justify-content:flex-end;margin-top:12px">
          <div style="min-width:280px">
            <div style="display:flex;justify-content:space-between;padding:6px 0;color:#64748b"><span>Subtotal</span><span>${fmtMoney(
          subtotal
        )}</span></div>
            <div style="display:flex;justify-content:space-between;padding:6px 0;color:#64748b"><span>Delivery Fee</span><span>${fmtMoney(
          deliveryFee
        )}</span></div>
            <div style="display:flex;justify-content:space-between;padding:6px 0;color:#64748b"><span>Discount</span><span>${fmtMoney(
          discountValue
        )}</span></div>
            <div style="height:1px;background:#e5e7eb;margin:6px 0" />
            <div style="display:flex;justify-content:space-between;padding:6px 0;font-weight:700"><span>Total</span><span>${fmtMoney(
          total
        )}</span></div>
          </div>
        </div>

        <div style="margin-top:12px;color:#64748b;font-size:12px">Notes: ${order?.notes ?? ""
      }</div>
      </div>
    `;
  }, [
    order,
    customer,
    items,
    created,
    subtotal,
    deliveryFee,
    discountValue,
    total,
  ]);

  const handlePrintInvoice = async () => {
    openPrintWindow(printableInvoiceHtml, `Invoice #${order?.id ?? "-"}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Invoice #{String(order?.id ?? "-")}
            </span>
            <div className="flex items-center gap-2">
              {order?.status && (
                <Badge variant="secondary">{order.status}</Badge>
              )}
              {order?.paymentStatus && (
                <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
                  {order.paymentStatus}
                </Badge>
              )}
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Invoice details
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[80vh] px-6 pb-2">
          <Card className="mt-2">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3 min-w-[220px]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center overflow-hidden">
                    {data?.customer?.profileImageUrl ? (
                      <img
                        src={data.customer.profileImageUrl}
                        className="w-10 h-10 object-cover"
                      />
                    ) : (
                      <span className="text-white font-semibold">
                        {data?.customer?.name?.[0] ?? "?"}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {data?.customer?.name ?? "-"}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Customer since{" "}
                      {fmtDateTime(data?.customer?.createdAt || '').split(",")[0]}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Phone</p>
                    <p className="text-sm text-gray-600">
                      {data?.customer?.phone || "Not provided"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Shipping Address
                    </p>
                    <p className="text-sm text-gray-600 whitespace-pre-line max-w-[28rem]">
                      {data?.customer?.address || "Not provided"}
                    </p>
                  </div>
                </div>
                <div className="ml-auto text-right min-w-[180px]">
                  <div className="text-xs text-muted-foreground">Created</div>
                  <div className="text-sm font-medium">
                    {fmtDateTime(created!)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="h-4 w-4" />
                <div className="text-sm font-semibold">Items</div>
              </div>
              <div className="border rounded-lg divide-y">
                <div className="grid grid-cols-12 gap-3 px-3 py-2 text-xs text-muted-foreground">
                  <div className="col-span-6">Product</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-2 text-right">Unit</div>
                  <div className="col-span-2 text-right">Amount</div>
                </div>
                {(items ?? []).map((it, idx) => {
                  const qty = Number(it.quantity ?? 0);
                  const price = Number(it.price ?? 0);
                  const line =
                    it.amount != null ? Number(it.amount) : qty * price;
                  const name = (it as any).productName ?? it.productId ?? "-";
                  return (
                    <div
                      key={idx}
                      className="grid grid-cols-12 gap-3 px-3 py-2 text-sm"
                    >
                      <div className="col-span-6 truncate" title={name}>
                        {name}
                      </div>
                      <div className="col-span-2 text-right">
                        {Number.isFinite(qty) ? qty : "-"}
                      </div>
                      <div className="col-span-2 text-right">
                        {fmtMoney(price)}
                      </div>
                      <div className="col-span-2 text-right font-medium">
                        {fmtMoney(line)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Receipt className="h-4 w-4" /> Payment
                </div>
                <div className="text-sm flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span>{order?.paymentStatus ?? "-"}</span>
                </div>
                <div className="text-sm flex justify-between">
                  <span className="text-muted-foreground">Source</span>
                  <span>{order?.source ?? "-"}</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="text-sm font-semibold">Notes</div>
                <div className="text-sm text-muted-foreground whitespace-pre-wrap min-h-[48px]">
                  {order?.notes || "-"}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="text-sm font-semibold">Shipping</div>
                <div className="text-sm flex justify-between">
                  <span className="text-muted-foreground">Tracking</span>
                  <span
                    className="truncate max-w-[160px]"
                    title={order?.trackingNumber || "NA"}
                  >
                    {order?.trackingNumber ?? "-"}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-4 mb-2">
            <CardContent className="p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{fmtMoney(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delivery Fee</span>
                <span>{fmtMoney(deliveryFee)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Discount</span>
                <span>{fmtMoney(discountValue)}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold text-base">
                <span>Total</span>
                <span>{fmtMoney(total)}</span>
              </div>
            </CardContent>
          </Card>
        </ScrollArea>

        <DialogFooter className="px-6 pb-6 gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            Close
          </Button>
          <Button onClick={handlePrintInvoice}>
            <Printer className="w-4 h-4 mr-2" />
            Print Invoice
          </Button>
          <ReceiptTicket
            data={data!}
            title="NorthCare Mart"
            footer="សូមអរគុណ! Thank you"
          />
          <ShippingLabelReceipt
            data={data!}
            qrValue={`https://pos.example.com/orders/${order?.id}`}
            size="80mm"
            store={{ name: "Charming Store" }}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- ReceiptTicket (80mm thermal style) ----------------
export function ReceiptTicket({
  data,
  title = "Store Name",
  footer = "Thank you!",
}: {
  data: TOrderWithRelation;
  title?: string;
  footer?: string;
}) {
  const order = data.order;
  const customer = data.customer;
  const items = data.orderItems ?? [];

  const computedSubtotal = items.reduce((acc, it) => {
    const q = Number(it.quantity ?? 0);
    const p = Number(it.price ?? 0);
    const line = it.amount != null ? Number(it.amount) : q * p;
    return acc + (Number.isFinite(line) ? line : 0);
  }, 0);

  const subtotal = order?.subTotal ?? computedSubtotal;
  const deliveryFee = Number(order?.deliveryFee ?? 0);
  const discountValue = order?.discountPercentage
    ? (Number(subtotal) * Number(order.discountPercentage)) / 100
    : Number(order?.discountPercentage ?? 0);
  const total =
    order?.totalAmount ?? Number(subtotal) + deliveryFee - discountValue;

  // build minimalist receipt HTML for thermal printers
  const html = `
  <style>
    @page { size: 80mm auto; margin: 4mm; }
    body { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 12px; }
    .center { text-align: center; }
    .row { display:flex; justify-content:space-between; }
    .muted { color:#64748b; }
    hr { border:0; border-top:1px dashed #e2e8f0; margin:6px 0; }
    table { width:100%; border-collapse: collapse; }
    td { vertical-align: top; padding: 2px 0; }
  </style>
  <div class="center">
    <div style="font-weight:700">${title}</div>
    <div class="muted">Invoice #${order?.id ?? "-"}</div>
    <div class="muted">${fmtDateTime(
    order?.createdAt
  )}</div>
  </div>
  <hr/>
  <div>
    <div class="row"><span>Customer</span><span>${customer?.name ?? "-"
    }</span></div>
    ${customer?.phone
      ? `<div class="row"><span>Phone</span><span>${customer.phone}</span></div>`
      : ""
    }
  </div>
  <hr/>
  <table>
    ${items
      .map((it) => {
        const qty = Number(it.quantity ?? 0);
        const price = Number(it.price ?? 0);
        const line = it.amount != null ? Number(it.amount) : qty * price;
        const name = (it as any).productName ?? it.productId ?? "-";
        return `<tr><td>${name} x${qty}</td><td style="text-align:right">${fmtMoney(
          line
        )}</td></tr>`;
      })
      .join("")}
  </table>
  <hr/>
  <div class="row"><span class="muted">Subtotal</span><span>${fmtMoney(
        subtotal
      )}</span></div>
  <div class="row"><span class="muted">Delivery</span><span>${fmtMoney(
        deliveryFee
      )}</span></div>
  <div class="row"><span class="muted">Discount</span><span>${fmtMoney(
        discountValue
      )}</span></div>
  <div class="row" style="font-weight:700"><span>Total</span><span>${fmtMoney(
        total
      )}</span></div>
  <hr/>
  <div class="center">${footer}</div>
  `;

  const handlePrint = () => {
    openPrintWindow(html, `Receipt #${order?.orderNumber ?? "-"}`);

  };

  return (
    <div className="flex items-center gap-2">
      <Button size="sm" onClick={handlePrint}>
        <Printer className="w-4 h-4 mr-2" />
        Print Receipt
      </Button>
    </div>
  );
}

// ---------------- QR helpers ----------------
// Lightweight QR rendering via react-qr-code (SVG, print-friendly)
// npm i react-qr-code
import QRCode from "react-qr-code";
import { printOrder } from "@/api/order-api";

// ---------------- ShippingLabelReceipt (A6/80mm label with QR) ----------------
export function ShippingLabelReceipt({
  data,
  page = { index: 1, total: 1 },
  qrValue,
  size = "80mm", // "80mm" | "A6"
  store = { name: "Charming Store" },
}: {
  data: TOrderWithRelation;
  page?: { index: number; total: number };
  qrValue: string; // e.g., order deep link or payment URL
  size?: "80mm" | "A6";
  store?: { name: string; address?: string; phone?: string };
}) {
  const order = data.order;
  const customer = data.customer;
  const items = data.orderItems ?? [];
  const qtyTotal = items.reduce((s, it) => s + Number(it.quantity ?? 0), 0);

  // Build compact, printer-friendly layout
  const pageCss =
    size === "A6"
      ? "@page { size: A6; margin: 6mm; }"
      : "@page { size: 80mm auto; margin: 4mm; }";

  const html = `
  <style>
    ${pageCss}
    *{ box-sizing: border-box; }
    body{ font-family: ui-sans-serif, -apple-system, Segoe UI, Roboto, Noto Sans, Noto Sans Khmer, sans-serif; font-size:12px; color:#111827; }
    h1{ font-size:14px; margin:0 0 6px; font-weight:700; }
    .muted{ color:#6b7280; }
    .row{ display:flex; justify-content:space-between; align-items:flex-start; gap:8px; }
    .box{ border:1px solid #e5e7eb; border-radius:8px; padding:10px; margin-bottom:8px; }
    table{ width:100%; border-collapse:collapse; }
    th,td{ padding:6px 6px; border-top:1px solid #f3f4f6; text-align:left; }
    th:nth-child(2), td:nth-child(2){ text-align:center; width:48px; }
    th:nth-child(3), td:nth-child(3){ text-align:right; width:64px; }
    .footer{ display:flex; justify-content:space-between; margin-top:6px; }
    .small{ font-size:11px; }
    .qr{ width:84px; height:84px; }
  </style>
  <div style="max-width:420px;margin:0 auto">
    <div class="row" style="margin-bottom:6px">
      <div>
        <div class="small muted">Order#</div>
        <h1>${order?.id ?? "-"}</h1>
        <div class="small muted">Date: ${fmtDateTime(
    order?.createdAt
  )}</div>
        <div class="small muted">From: ${store.name}</div>
      </div>
      <div id="qr-slot"></div>
    </div>

    <div class="box">
      <div class="small muted" style="margin-bottom:4px">To:</div>
      <div style="font-weight:600">${customer?.name ?? "-"}</div>
      ${customer?.phone ? `<div>${customer.phone}</div>` : ""}
      ${customer?.address
      ? `<div class="muted" style="white-space:pre-line">${customer.address}</div>`
      : ""
    }

    </div>

    <table>
      <thead>
        <tr><th>No.</th><th>Qty</th><th>Description</th></tr>
      </thead>
      <tbody>
        ${items
      .map((it, idx) => {
        const qty = Number(it.quantity ?? 0);
        const name = (it as any).productName ?? it.productId ?? "-";
        return `<tr><td>${idx + 1
          }</td><td>${qty}</td><td style="text-align:left">${name}</td></tr>`;
      })
      .join("")}
      </tbody>
    </table>

    <div class="footer">
      <div class="small muted">Total</div>
      <div style="font-weight:700">${qtyTotal}</div>
      <div class="small muted">Page</div>
      <div class="small">${page.index}/${page.total}</div>
    </div>
  </div>`;

  const onPrint = () => {
    // Render with iframe and inject QR SVG dynamically
    const docHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Label ${order?.id ?? ""
      }</title></head><body>${html}</body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(docHtml);
    doc.close();

    // Inject QR as SVG into #qr-slot so it prints crisply
    const mountQr = () => {
      const slot = doc.getElementById("qr-slot");
      if (!slot) return;
      const svg = doc.createElementNS("http://www.w3.org/2000/svg", "svg");
      // use a simple inline QR via react-qr-code equivalent path (fallback)
      // For highest fidelity, you can server-render or draw via a small QR lib.
      // Here we just place a placeholder in case you don't SSR the component.
      // You can also skip this section and let the app window render with <QRCode />
      svg.setAttribute("width", "84");
      svg.setAttribute("height", "84");
      svg.setAttribute("viewBox", "0 0 84 84");
      const txt = doc.createElementNS("http://www.w3.org/2000/svg", "text");
      txt.setAttribute("x", "6");
      txt.setAttribute("y", "46");
      txt.textContent = "QR"; // placeholder if not SSR
      svg.appendChild(txt);
      slot.appendChild(svg);

      // Print after mount
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      }, 300);
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
        } catch { }
      }, 800);
    };

    // If DOM ready
    if (doc.readyState === "complete") mountQr();
    else doc.addEventListener("DOMContentLoaded", mountQr, { once: true });
  };

  return (
    <div className="flex items-center gap-2">
      {/* On-screen preview header with live QR */}
      <div className="hidden print:block" />
      <Button type="button" onClick={onPrint}>
        <Printer className="w-4 h-4 mr-2" />
        Print Label
      </Button>
      {/* Optional on-screen preview block (not printed) */}
      <div className="no-print hidden md:block border rounded-lg p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs text-muted-foreground">Order#</div>
            <div className="font-semibold">{order?.id}</div>
            <div className="text-xs text-muted-foreground">
              {fmtDateTime(order?.createdAt)}
            </div>
          </div>
          <QRCode value={qrValue} size={84} />
        </div>
      </div>
    </div>
  );
}

// ---------------- Example usage ----------------
// <InvoiceDialog open={open} onOpenChange={setOpen} data={orderWithRelation} />
// <ReceiptTicket data={orderWithRelation} title="NorthCare Mart" footer="សូមអរគុណ! Thank you" />
// <ShippingLabelReceipt data={orderWithRelation} qrValue={`https://pos.example.com/orders/${orderWithRelation.order.id}`} size="80mm" store={{ name: "Charming Store" }} />
